import React from 'react';
import DOMPurify from 'dompurify';
import { BlockMath, InlineMath } from 'react-katex';
import ReactMarkdown from 'react-markdown';
import { dark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeExternalLinks from 'rehype-external-links';
import 'katex/dist/katex.min.css';
import DCopyBtn from '@/components/Global/DCopyBtn';
import './bubbleMarkdown.css';

const SyntaxHighlighter = React.lazy(() =>
  import('react-syntax-highlighter/dist/esm/prism')
);

// Simple error boundary for syntax highlighter
class SyntaxHighlighterErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.warn('SyntaxHighlighter error, falling back to plain code:', error);
  }

  render() {
    if (this.state.hasError) {
      // Fallback to simple code block
      return (
        <pre className="custom-syntax-highlighter">
          <code>{this.props.children}</code>
        </pre>
      );
    }

    return this.props.children;
  }
}

const Pre = ({ children }) => (
  <pre className="blog-pre">
    <DCopyBtn>{children}</DCopyBtn>
    {/* {children} */}
  </pre>
);

const OrderList = ({ children, start }) => (
  <ol
    className="ml-[1.7rem] list-outside list-decimal space-y-1 !mt-2 !mb-3"
    style={{
      color: 'var(--dt-color-element-75)',
      fontSize: 'var(--dt-font-size)',
      lineHeight: 'var(--dt-line-height)'
    }}
    start={start}
  >
    {children}
  </ol>
);
const UnorderedList = ({ children }) => (
  <ul
    className="ml-[1.25rem] list-outside list-disc space-y-1 !mt-2 !mb-3"
    style={{
      fontSize: 'var(--dt-font-size)',
      lineHeight: 'var(--dt-line-height)',
      color: 'var(--dt-color-element-100)'
    }}
  >
    {children}
  </ul>
);
const ListItem = ({ children }) => (
  <li
    className="!my-1 !mb-2"
    style={{
      fontSize: 'var(--dt-font-size)',
      lineHeight: 'var(--dt-line-height)',
      color: 'var(--dt-color-element-100)'
    }}
  >
    {children}
  </li>
);
const Paragraph = ({ children }) => (
  <p
    className="[&:not(:last-child)]:mb-size2 [&:not(:first-child)]:mt-size1"
    style={{
      fontSize: 'var(--dt-font-size)',
      lineHeight: 'var(--dt-line-height)'
    }}
  >
    {children}
  </p>
);
const Link = ({ children, href, target, kb_id, role, ...props }) => {
  // Special handling for specific kb_ids with user role
  const isSpecialUserCase = (kb_id === 'ca7ac107-9716-4ef3-a6b5-55ceab49f15a' || kb_id === '86927718-7690-4c0c-a99d-8bc8afda0a4c') && role === 'user';

  // Ensure URL has protocol and always open in new tab
  const processHref = (url) => {
    if (!url) return url;
    
    // If URL already has a protocol, return as is
    if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url)) {
      return url;
    }
    
    // Otherwise, add https://
    return `https://${url}`;
  };

  const processedHref = processHref(href);

  return (
    <a
      className={`break-all  ${
        isSpecialUserCase
          ? '!text-[#fff] underline hover:!text-[#e0e0e0] transition-colors duration-200'
          : role === 'user'
          ? '!text-[var(--dt-color-surface-100)] underline hover:!text-[var(--dt-color-surface-15)] transition-colors duration-200'
          : 'text-purple-300 underline hover:text-purple-200 transition-colors duration-200'
      }`}
      href={processedHref}
      target="_blank"
      rel="noreferrer"
      {...props}
    >
      {children}
    </a>
  );
};

const HorizontalRule = () => <hr className="my-4 border-t border-gray-300" />;

// Heading components with dynamic line height
const Heading1 = ({ children }) => (
  <h1
    className="text-xl font-bold mb-size2 mt-size3"
    style={{
      fontSize: 'calc(var(--dt-font-size) * 1.5)',
      lineHeight: 'var(--dt-line-height)'
    }}
  >
    {children}
  </h1>
);

const Heading2 = ({ children }) => (
  <h2
    className="text-lg font-bold mb-size2 mt-size3"
    style={{
      fontSize: 'calc(var(--dt-font-size) * 1.25)',
      lineHeight: 'var(--dt-line-height)'
    }}
  >
    {children}
  </h2>
);

const Heading3 = ({ children }) => (
  <h3
    className="text-base font-bold mb-size1 mt-size2"
    style={{
      fontSize: 'calc(var(--dt-font-size) * 1.125)',
      lineHeight: 'var(--dt-line-height)'
    }}
  >
    {children}
  </h3>
);

const Heading4 = ({ children }) => (
  <h4
    className="text-base font-medium mb-size1 mt-size2"
    style={{
      fontSize: 'var(--dt-font-size)',
      lineHeight: 'var(--dt-line-height)'
    }}
  >
    {children}
  </h4>
);

const Heading5 = ({ children }) => (
  <h5
    className="text-sm font-medium mb-size1 mt-size2"
    style={{
      fontSize: 'calc(var(--dt-font-size) * 0.875)',
      lineHeight: 'var(--dt-line-height)'
    }}
  >
    {children}
  </h5>
);

const Heading6 = ({ children }) => (
  <h6
    className="text-sm font-medium mb-size1 mt-size2"
    style={{
      fontSize: 'calc(var(--dt-font-size) * 0.75)',
      lineHeight: 'var(--dt-line-height)'
    }}
  >
    {children}
  </h6>
);

const TableRenderer = ({ children }) => {
  return (
    <div className="overflow-x-auto my-4">
      <table className="border-collapse table-auto w-full text-sm">
        {children}
      </table>
    </div>
  );
};

const TableHeader = ({ children }) => {
  return (
    <th className="border-b border-gray-300 p-size1 text-left">{children}</th>
  );
};

const TableCell = ({ children }) => {
  return (
    <td className="border-b border-gray-300 p-size1 text-left">{children}</td>
  );
};

const ImageRenderer = ({ src, alt }) => {
  return <img src={src} alt={alt} className="custom-markdown-image" />;
};

const handleMathTags = (content) => {
  const latexCommands = [
    // Functions
    'int', 'sum', 'prod', 'lim', 'log', 'ln', 'exp', 'sin', 'cos', 'tan', 'cot', 'sec', 'csc',
    'arcsin', 'arccos', 'arctan', 'max', 'min', 'sup', 'inf', 'deg', 'det', 'dim', 'gcd',
    'hom', 'ker', 'Pr', 'mod',
  
    // Operators & Symbols
    'cdot', 'times', 'div', 'pm', 'mp', 'frac', 'sqrt', 'leq', 'geq', 'neq', 'approx', 'asymp',
    'equiv', 'propto', 'infty', 'partial', 'nabla', 'rightarrow', 'leftarrow', 'leftrightarrow',
    'Rightarrow', 'Leftarrow', 'Leftrightarrow', 'to', 'mapsto', 'longrightarrow',
    'longleftarrow', 'hookrightarrow', 'hookleftarrow', 'uparrow', 'downarrow', 'updownarrow',
  
    // Logic & Sets
    'land', 'lor', 'neg', 'lnot', 'in', 'notin', 'ni', 'subset', 'supset', 'subseteq', 'supseteq',
    'cup', 'cap', 'setminus', 'emptyset', 'forall', 'exists', 'therefore', 'because',
    'vdash', 'dashv', 'models',
  
    // Greek Letters (lowercase and uppercase)
    'alpha', 'beta', 'gamma', 'delta', 'epsilon', 'varepsilon', 'zeta', 'eta', 'theta', 'vartheta',
    'iota', 'kappa', 'lambda', 'mu', 'nu', 'xi', 'pi', 'varpi', 'rho', 'varrho', 'sigma',
    'varsigma', 'tau', 'upsilon', 'phi', 'varphi', 'chi', 'psi', 'omega',
    'Gamma', 'Delta', 'Theta', 'Lambda', 'Xi', 'Pi', 'Sigma', 'Upsilon', 'Phi', 'Psi', 'Omega',
  
    // Accents
    'hat', 'bar', 'tilde', 'vec', 'dot', 'ddot', 'check', 'breve', 'acute', 'grave',
    'overline', 'underline',
  
    // Fonts and Text
    'text', 'mathrm', 'mathcal', 'mathbb', 'mathbf', 'mathit', 'mathtt', 'mathsf', 'boldsymbol',
  
    // Absolute value and delimiters
    'lvert', 'rvert', 'lVert', 'rVert', 'vert', 'Vert', 'lbrack', 'rbrack', 'langle', 'rangle',
    'lceil', 'rceil', 'lfloor', 'rfloor', 'left', 'right',
  
    // Brackets and Delimiter Shorthands
    'big', 'Big', 'bigg', 'Bigg', 'bigl', 'bigr', 'Bigl', 'Bigr',
  
    // Matrix commands
    'matrix', 'pmatrix', 'bmatrix', 'vmatrix', 'Vmatrix', 'smallmatrix', 'array', 'cases',
    'align', 'aligned', 'alignat', 'alignedat', 'eqnarray', 'eqnarray*', 'split', 'gather', 'gathered',
    'multline', 'multline*', 'flalign', 'flalign*', 'align*', 'eqnarray*', 'gather*', 'multline*',
    'begin', 'end', '&', '\\\\', '\\cr', '\\hline', '\\hline\\hline',
  
    // Other
    'boxed', 'cancel', 'binom', 'tbinom', 'dbinom', 'choose', 'stackrel', 'overset', 'underset',
    'not', 'phantom', 'hphantom', 'vphantom', 'dots', 'cdots', 'ldots', 'vdots', 'ddots',
    'quad', 'qquad', 'tag', ',', '.', ':', ';', '!', '%'
  ];
  
  
  const mathTags = ['dante_math', 'dante_formula'];
  let fixedContent = content;

  for (const tag of mathTags) {
    if (fixedContent.includes(`<${tag}>`)) {
      const mathRegex = new RegExp(`<${tag}>(.*?)</${tag}>`, 'gs');
      let match;
      let tempContent = fixedContent;

      while ((match = mathRegex.exec(fixedContent)) !== null) {
        let mathContent = match[1];

        latexCommands.forEach((cmd) => {
          const pattern = new RegExp(`"${cmd}\\b`, 'g');
          mathContent = mathContent.replace(pattern, `\\${cmd}`);
        });

        mathContent = mathContent.replace(/"([^a-zA-Z])/g, '$1');

        mathContent = mathContent.replace(/"/g, '');

        mathContent = mathContent.replace(/\$/g, '');

        tempContent = tempContent.replace(match[0], `$$$${mathContent}$$$`);
      }
      fixedContent = tempContent;
    }
  }
  fixedContent = fixedContent.replaceAll('__DOLLAR_SIGN__', '$');
  return fixedContent;
};

const cleanDanteImageTags = (content) => {
  if (!content.includes('<dante_image>')) return content;

  const imageRegex = /<dante_image>(.*?)<\/dante_image>/gs;
  let match;
  let tempContent = content;

  while ((match = imageRegex.exec(content)) !== null) {
    let imageContent = match[1].trim();
    tempContent = tempContent.replace(match[0], imageContent);
  }

  return tempContent;
};

const squashWhitespace = (str) => str.replace(/\s+/g, '').trim();

const cleanDanteUrlTags = (content) => {
  if (!content.includes('<dante_url>')) return content;

  return content.replace(
    /<dante_url>([\s\S]*?)<\/dante_url>/g,                  // capture inner text
    (_, raw) => `<${squashWhitespace(raw)}>`                // <url> → GFM autolink
  );
};


const sanitizeContent = (content, isUser) => {
  if (!content) return '';

  let fixedContent = content;

  // Handle dante math tags
  fixedContent = handleMathTags(fixedContent);

  // Handle dante_table tags before other processing
  if (fixedContent.includes('<dante_table>')) {
    const parts = fixedContent.split('<dante_table>');
    let result = parts[0]; // Keep content before the table

    for (let i = 1; i < parts.length; i++) {
      const tablePart = parts[i];
      if (tablePart.includes('</dante_table>')) {
        // Split at closing tag and keep both table content and remaining content
        const [tableContent, remainingContent] =
          tablePart.split('</dante_table>');
        result += tableContent.trim() + (remainingContent || '');
      } else {
        // If no closing tag, keep the content as is
        result += tablePart;
      }
    }
    fixedContent = result;
  }

  if (fixedContent.includes('<dante_image>')) {
    fixedContent = cleanDanteImageTags(fixedContent);
  }

  if (fixedContent.includes('<dante_url>')) {
    fixedContent = cleanDanteUrlTags(fixedContent);
  }

  // Preserve horizontal rules by replacing them with a special marker
  fixedContent = fixedContent
    .replace(/^\s*-{3,}\s*$/gm, '___HORIZONTAL_RULE___')
    .trim();

  // Split by newlines, preserve empty lines for spacing
  const lines = fixedContent.split('\n');

  // Process lines to maintain proper spacing
  const processedLines = [];
  let previousLineEmpty = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Handle horizontal rule markers
    if (trimmedLine === '___HORIZONTAL_RULE___') {
      processedLines.push('---');
      continue;
    }

    // If this is an empty line
    if (trimmedLine.length === 0) {
      // Only add one empty line (avoid multiple consecutive empty lines)
      if (!previousLineEmpty) {
        processedLines.push('');
        previousLineEmpty = true;
      }
    } else {
      // This is a non-empty line
      processedLines.push(line);
      previousLineEmpty = false;
    }
  }

  // Join the processed lines back together
  fixedContent = processedLines.join('\n');

  // Use DOMPurify only for user-generated content
  if (isUser) {
    fixedContent = DOMPurify.sanitize(fixedContent, { 
      ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u',  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'span'],
    ALLOWED_ATTR: [],
    ALLOWED_PROTOCOLS: ['https'],
   });
  }

  // Escape isolated dollar signs that aren't part of a math expression
  // fixedContent = fixedContent.replace(/([^\\])\$(?!\d)/g, '$1\\$');
  return fixedContent;
};

const generateMarkdownBubble = (urlTransform, ele, kb_id) => {
  const isUser = ele?.role === 'user';
  const content = sanitizeContent(ele?.content, isUser);

  return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[
          rehypeRaw,
          rehypeKatex,
        ]}
      components={{
        h1: Heading1,
        h2: Heading2,
        h3: Heading3,
        h4: Heading4,
        h5: Heading5,
        h6: Heading6,
        table: TableRenderer,
        th: TableHeader,
        td: TableCell,
        p: Paragraph,
        pre: Pre,
        a: ({ children, href, ...props }) => (
          <Link href={href} kb_id={kb_id} role={ele?.role} {...props}>
            {children}
          </Link>
        ),
        ul: UnorderedList,
        ol: ({ start, children, ...props }) => (
          <OrderList start={start} {...props}>
            {children}
          </OrderList>
        ),
        li: ListItem,
        img: ImageRenderer,
        hr: HorizontalRule,
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          return !inline && match ? (
            <SyntaxHighlighterErrorBoundary>
              <React.Suspense fallback={
                <pre className="custom-syntax-highlighter">
                  <code>{String(children).replace(/\n$/, '')}</code>
                </pre>
              }>
                <SyntaxHighlighter
                  {...props}
                  style={dark}
                  language={match[1]}
                  PreTag="div"
                  className="custom-syntax-highlighter"
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              </React.Suspense>
            </SyntaxHighlighterErrorBoundary>
          ) : (
            <code {...props} className={className}>
              {children}
            </code>
          );
        },
        math: ({ value }) => {
          return <BlockMath>{value}</BlockMath>;
        },
        inlineMath: ({ value }) => {
          return <InlineMath>{value}</InlineMath>;
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default generateMarkdownBubble;
